import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { RestService } from '../../shared/rest.service';

/**
 * Resuelve la URL de imagen de un destino consultando el backend cuando
 * el summary del itinerario no trae `inmg_zones` (o las trae vacías).
 *
 * Cachea la respuesta por `destination_id` para evitar llamadas repetidas
 * cuando el mismo destino aparece varias veces en la UI.
 */
@Injectable()
export class DestinationImageService {
  private cache = new Map<string | number, Observable<string | null>>();

  constructor(private restService: RestService) {}

  /**
   * Devuelve un Observable que emite UNA URL de imagen para el destino,
   * o `null` si el backend no devuelve nada / falla.
   *
   * Mismo id → mismo Observable (cacheado con `shareReplay`).
   */
  getImageByDestinationId(id: string | number | null | undefined): Observable<string | null> {
    if (id === null || id === undefined || id === '') {
      return of(null);
    }

    const cached = this.cache.get(id);
    if (cached) {
      return cached;
    }

    const request$ = this.restService
      .doGet('app', 'itineraries/destination-images', { params: { destination_id: id } })
      .pipe(
        // TODO: ajustar el campo exacto (images[0] / data[0].url / etc.) según
        // la respuesta real que devuelva Octopus. Por ahora logueamos la
        // respuesta cruda para que el usuario confirme la forma.
        map((response: any) => this.extractImageUrl(response)),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.cache.set(id, request$);
    return request$;
  }

  /**
   * Extrae la URL de la respuesta de Octopus.
   * Ajustar según el shape real — ver TODO arriba.
   */
  private extractImageUrl(response: any): string | null {
    // TODO: remove once response shape is confirmed by user
    // eslint-disable-next-line no-console
    console.log('[DestinationImageService] raw response', response);

    if (!response) {
      return null;
    }

    // Hipótesis 1: { success, data: { images: [url, ...] } }
    if (Array.isArray(response?.data?.images) && response.data.images.length > 0) {
      return response.data.images[0];
    }

    // Hipótesis 2: { success, data: { url: '...' } }
    if (typeof response?.data?.url === 'string' && response.data.url) {
      return response.data.url;
    }

    // Hipótesis 3: array directo [{ url }, ...]
    if (Array.isArray(response) && response.length > 0) {
      const first = response[0];
      if (typeof first === 'string') {
        return first;
      }
      if (typeof first?.url === 'string') {
        return first.url;
      }
      if (typeof first?.image === 'string') {
        return first.image;
      }
    }

    // Hipótesis 4: { images: [url, ...] }
    if (Array.isArray(response?.images) && response.images.length > 0) {
      return response.images[0];
    }

    return null;
  }
}
